// src/components/common/popup-subscription.jsx
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import { Link, useStaticQuery, graphql } from "gatsby";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9998;
`;

const PopupContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 440px;
  padding: 20px;
  padding-top: 28px;
  z-index: 9999;
  font-family: "Big Caslon", serif;
`;

const PopupContent = styled.div`
  width: 400px;
  border-radius: 1rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  background: #fff;
`;

const PopupHeader = styled.div`
  background: #000;
  color: #fff;
  height: 150px;
  text-align: center;
  font-family: "Kinder", sans-serif;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const PopupTitle = styled.h2`
  margin: 0;
  font-family: "Kinder", sans-serif;
  font-size: 2rem;
  line-height: 0.9;
  max-width: 280px;
  white-space: pre-line;
`;

const PopupSubtitle = styled.p`
  font-family: "Big Caslon", serif;
  margin: 8px 0 0 0;
  font-size: 1.2rem;
  font-weight: 400;
`;

const PopupBody = styled.div`
  background: #fff;
  color: #000;
  padding: 32px;
  text-align: center;
  font-family: "Libre Baskerville", serif;
  height: calc(100% - 150px);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-top: 48px;
`;

const Description = styled.p`
  margin: 0;
  line-height: 1;
  font-weight: bold;
  font-size: 1.1rem;
  max-width: 300px;
  margin: 0 auto;
`;

const SecondaryDescription = styled.p`
  margin: 24px 0 0 0;
  line-height: 1.3;
  font-weight: bold;
  font-size: 0.9rem;
  max-width: 300px;
  margin: 24px auto 0;
  white-space: pre-line;
`;

const FormGroup = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 40px;

  input {
    font-family: "Big Caslon", serif;
    border: 1px solid #000;
    border-top: 1px solid #e0e0e0;
    border-right: 0px solid #fff;
    padding: 12px;
    width: 180px;
    color: #000;
    font-size: 0.9rem;
    font-weight: bold;

    &::placeholder {
      color: #ccc;
    }

    &:disabled {
      background-color: #f5f5f5;
      cursor: not-allowed;
    }
  }

  button {
    background-color: #fff;
    color: #ccc;
    border: 1px solid #000;
    border-top: 1px solid #e0e0e0;
    padding: 12px 16px;
    cursor: pointer;
    font-family: "Big Caslon", serif;
    font-size: 0.9rem;
    font-weight: bold;
    transition: all 0.3s ease;

    &:hover:not(:disabled) {
      background-color: #333;
      color: #fff;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.7;
    }
  }
`;

const SubscriptionStatus = styled.p`
  margin: 8px 0 0;
  font-size: 0.9rem;
  color: ${(props) => (props.error ? "#ff0000" : "#666")};
  font-family: "Big Caslon", serif;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 10px;
  background: white;
  border: none;
  color: #000;
  font-size: 1.2rem;
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  z-index: 10000;
  font-weight: 300;
  line-height: 0;
  padding: 0;

  &:hover {
    background: #f5f5f5;
  }
`;

const Privacy = styled.div`
  margin-top: 16px;
  text-align: left;
  font-family: "Big Caslon", serif;
`;

const CheckboxContainer = styled.label`
  display: flex;
  align-items: flex-start;
  cursor: pointer;
  user-select: none;
`;

const CheckboxInput = styled.input`
  margin: 3px 8px 0 0;
  cursor: pointer;
`;

const PrivacyText = styled.span`
  font-size: 0.875rem;
  color: #000;
  font-family: "Big Caslon", serif;
  line-height: 1.4;

  a {
    color: #000;
    text-decoration: underline;

    &:hover {
      opacity: 0.8;
    }
  }
`;

const ErrorMessage = styled.p`
  color: #ff0000;
  font-size: 0.8rem;
  margin: 8px 0 0;
  text-align: center;
  font-family: "Big Caslon", serif;
`;

const PopupSubscription = ({ onSubmit: propOnSubmit }) => {
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("initial");
  const [checked, setChecked] = useState(false);
  const [showPrivacyError, setShowPrivacyError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const STORAGE_KEY = "newsletter-itsgreener";

  const data = useStaticQuery(graphql`
    query {
      prismicNewsletterPopup {
        data {
          title {
            text
          }
          subtitle {
            text
          }
          description {
            text
          }
          secondary_description {
            text
          }
        }
      }
    }
  `);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const hasSubscribed = localStorage.getItem(STORAGE_KEY) === "true";
    let path = window.location.pathname || "/";
    if (path.length > 1 && path.endsWith("/")) {
      path = path.slice(0, -1);
    }

    // Whitelist: no popup en /newsletter
    if (path === "/newsletter") {
      return;
    }

    // Si ya está suscrito, no mostrar popup en ningún caso
    if (hasSubscribed) {
      return;
    }

    // Home: abrir sin delay (respetando localStorage, ya comprobado)
    if (path === "/") {
      setVisible(true);
      return;
    }

    // Resto rutas: comportamiento actual con delay
    const timer = setTimeout(() => {
      setVisible(true);
    }, 15000);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => setVisible(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!checked) {
      setShowPrivacyError(true);
      return;
    }
    setShowPrivacyError(false);
    setErrorMessage("");

    if (email && status === "initial" && propOnSubmit) {
      setStatus("sending");
      const minimumWait = new Promise((resolve) => setTimeout(resolve, 1500));
      try {
        const [, response] = await Promise.all([
          minimumWait,
          propOnSubmit(email),
        ]);
        if (response.success) {
          setStatus("done");
          localStorage.setItem(STORAGE_KEY, "true");
          setTimeout(() => {
            handleClose();
          }, 2000);
        } else {
          setErrorMessage(
            response.error || "Something went wrong. Please try again."
          );
          setStatus("error");
        }
      } catch (e) {
        console.error("Newsletter subscription error:", e);
        setErrorMessage(
          "Unable to connect to the server. Please try again later."
        );
        setStatus("error");
      }
    }
  };

  const getButtonText = () => {
    switch (status) {
      case "sending":
        return "sending...";
      case "done":
        return "done!";
      case "error":
        return "error :(";
      default:
        return "send";
    }
  };

  if (!visible) return null;

  return (
    <>
      <Overlay onClick={handleClose} />
      <PopupContainer>
        <CloseButton onClick={handleClose} aria-label="Cerrar popup">
          ✕
        </CloseButton>
        <PopupContent>
          <PopupHeader>
            <PopupTitle>
              {data.prismicNewsletterPopup.data.title.text}
            </PopupTitle>
            <PopupSubtitle>
              {data.prismicNewsletterPopup.data.subtitle.text}
            </PopupSubtitle>
          </PopupHeader>
          <PopupBody>
            <Description>
              {data.prismicNewsletterPopup.data.description.text}
            </Description>
            <SecondaryDescription>
              {data.prismicNewsletterPopup.data.secondary_description.text}
            </SecondaryDescription>
            <form onSubmit={handleSubmit}>
              <FormGroup>
                <input
                  type="email"
                  placeholder="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={status !== "initial"}
                  required
                />
                <button
                  type="submit"
                  disabled={
                    status !== "initial" || !/[^@]+@[^.@]+\.[^@]+/.test(email)
                  }
                >
                  {getButtonText()}
                </button>
              </FormGroup>
              <Privacy>
                <CheckboxContainer>
                  <CheckboxInput
                    type="checkbox"
                    checked={checked}
                    onChange={(event) => {
                      setChecked(event.target.checked);
                      if (event.target.checked) setShowPrivacyError(false);
                    }}
                    name="privacy-policy"
                    required
                  />
                  <PrivacyText>
                    I have read and accept the{" "}
                    <Link to="/legal/privacy-policy">privacy policy</Link>.
                  </PrivacyText>
                </CheckboxContainer>
              </Privacy>
              {showPrivacyError && (
                <ErrorMessage>
                  Please accept the terms and conditions to continue.
                </ErrorMessage>
              )}
              {status === "error" && (
                <SubscriptionStatus error>{errorMessage}</SubscriptionStatus>
              )}
            </form>
          </PopupBody>
        </PopupContent>
      </PopupContainer>
    </>
  );
};

PopupSubscription.propTypes = {
  onSubmit: PropTypes.func,
};

export default PopupSubscription;
